import { TEMPLATE_ICONS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Dialog, DialogContent, Field, Icon, Input, Select, Skeleton } from "@sagot/ui";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { FormAlert } from "../../../auth/components/form-alert";
import { useErrorMessage } from "../../../lib/api-errors";
import { zodResolver } from "../../../lib/zod-resolver";
import { useWorkspaceDetails } from "../../../session/workspace-context";
import { useAudienceLabels, useTemplates } from "../shared/queries";
import type { AgentConfig, AgentSummary, IndustryTemplateKey } from "../shared/types";
import { AUDIENCE_CODES, PUBLIC_AUDIENCE, audiencesFor, defaultTemplate, hasMoreTemplates, orderTemplates, type PlanState } from "./agent-model";
import { useAgentActions } from "./use-agent-actions";

const NAME_MAX = 80;
const NO_COPY = "";

interface CreateValues {
  name: string;
  template_key: string;
  audience: string;
  copy_from: string;
}

interface CreateAgentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agents: readonly AgentSummary[];
  plan: PlanState;
  planName: string;
  onCreated: (agent: AgentConfig) => void;
}

function TemplateChoices({ value, onChange }: { value: string; onChange: (key: string) => void }) {
  const { t } = useTranslation();
  const templates = useTemplates();
  const industry = useWorkspaceDetails().data?.industry;
  const [showAll, setShowAll] = useState(false);
  if (templates.isPending) {
    return (
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" aria-hidden="true">
        {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
      </div>
    );
  }
  if (templates.isError) return <p className="text-sm text-warning">{t("build.agents.createDialog.templatesError")}</p>;
  const ordered = orderTemplates(templates.data, industry, showAll);
  return (
    <>
      <div role="group" aria-label={t("build.agents.createDialog.template")} className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {ordered.map((template) => (
          <button
            key={template.key}
            type="button"
            aria-pressed={template.key === value}
            onClick={() => { onChange(template.key); }}
            className="flex items-start gap-2.5 rounded-lg border border-line p-3 text-left hover:border-line-strong aria-pressed:border-primary aria-pressed:bg-primary-soft"
          >
            <Icon name={TEMPLATE_ICONS[template.key] ?? "sparkles"} className="mt-0.5 size-4 text-primary-ink" />
            <span className="min-w-0">
              <span className="flex flex-wrap items-center gap-1.5 text-sm font-bold">
                {template.name}
                {template.is_recommended ? <Badge tone="brand">{t("build.agents.createDialog.recommended")}</Badge> : null}
              </span>
              {template.description ? <span className="block text-[11px] text-fg-muted">{template.description}</span> : null}
            </span>
          </button>
        ))}
      </div>
      {hasMoreTemplates(templates.data, industry) ? (
        <Button type="button" variant="ghost" size="sm" className="mt-2" onClick={() => { setShowAll((v) => !v); }}>
          <Icon name={showAll ? "chevron-up" : "chevron-down"} />
          {t(showAll ? "build.agents.createDialog.showFewer" : "build.agents.createDialog.showAll")}
        </Button>
      ) : null}
    </>
  );
}

function useCreateSchema() {
  const { t } = useTranslation();
  return useMemo(
    () =>
      z.object({
        name: z.string().trim().min(1, t("build.agents.createDialog.nameRequired")).max(NAME_MAX),
        template_key: z.string().min(1),
        audience: z.string(),
        copy_from: z.string(),
      }),
    [t],
  );
}

/** `#new` dialog: name, industry template, audience, copy knowledge → `POST /v1/agents` (starts as Draft). */
export function CreateAgentDialog({ open, onOpenChange, agents, plan, planName, onCreated }: CreateAgentDialogProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const audienceLabel = useAudienceLabels();
  const templates = useTemplates();
  const industry = useWorkspaceDetails().data?.industry;
  const { create } = useAgentActions();
  const initialTemplate = defaultTemplate(orderTemplates(templates.data ?? [], industry, false), industry);
  const form = useForm<CreateValues>({
    resolver: zodResolver(useCreateSchema()),
    values: { name: "", template_key: initialTemplate, audience: String(PUBLIC_AUDIENCE), copy_from: NO_COPY },
    resetOptions: { keepDirtyValues: true },
  });
  const close = (next: boolean) => {
    if (!next) {
      create.reset();
      form.reset();
    }
    onOpenChange(next);
  };
  const submit = form.handleSubmit((values) => {
    create.mutate(
      {
        name: values.name.trim(),
        template_key: values.template_key as IndustryTemplateKey,
        audiences: audiencesFor(Number(values.audience)),
        copy_knowledge_from_agent_id: values.copy_from || null,
      },
      {
        onSuccess: (agent) => {
          close(false);
          onCreated(agent);
        },
      },
    );
  });
  const next = plan.used + 1;
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        title={t("build.agents.createDialog.title")}
        description={t("build.agents.createDialog.subtitle")}
        closeLabel={t("common.close")}
        className="max-w-xl"
        footer={
          <>
            <Button variant="ghost" onClick={() => { close(false); }}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" form="create-agent-form" loading={create.isPending}>
              <Icon name="sparkles" />
              {t("build.agents.createDialog.submit")}
            </Button>
          </>
        }
      >
        <form id="create-agent-form" noValidate className="max-h-[60vh] space-y-4 overflow-y-auto" onSubmit={(event) => void submit(event)}>
          {create.isError ? <FormAlert title={message(create.error)} /> : null}
          <Field label={t("build.agents.createDialog.name")} error={form.formState.errors.name?.message}>
            <Input placeholder={t("build.agents.createDialog.namePlaceholder")} maxLength={NAME_MAX} autoComplete="off" {...form.register("name")} />
          </Field>
          <fieldset>
            <legend className="label">{t("build.agents.createDialog.template")}</legend>
            <Controller control={form.control} name="template_key" render={({ field }) => <TemplateChoices value={field.value} onChange={field.onChange} />} />
          </fieldset>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label={t("build.agents.createDialog.audience")} hint={t("build.agents.createDialog.audienceHint")}>
              <Select {...form.register("audience")}>
                {AUDIENCE_CODES.map((code) => (
                  <option key={code} value={code}>
                    {code === PUBLIC_AUDIENCE
                      ? t("build.agents.createDialog.audiencePublic", { label: audienceLabel(code) })
                      : t("build.agents.createDialog.audienceOnly", { label: audienceLabel(code) })}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t("build.agents.createDialog.copy")} hint={t("build.agents.createDialog.copyHint")}>
              <Select {...form.register("copy_from")}>
                <option value={NO_COPY}>{t("build.agents.createDialog.copyNone")}</option>
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {t("build.agents.createDialog.copyOption", { name: agent.name, count: agent.stats.sources_count })}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="flex items-start gap-2.5 rounded-md bg-surface-2 px-3 py-2.5 text-xs text-fg-muted">
            <Icon name="info" className="mt-0.5 size-4 text-primary-ink" />
            <span>
              {plan.limit === null
                ? t("build.agents.createDialog.planNoteUnlimited")
                : t("build.agents.createDialog.planNote", { next, limit: plan.limit, plan: planName })}
            </span>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
